import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export function DeleteUserDialog({ target, onClose, onDeleted }) {
  const [pin, setPin] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (target) { setPin(""); setConfirmEmail(""); }
  }, [target]);

  const emailMatches = target &&
    confirmEmail.trim().toLowerCase() === (target.email || "").toLowerCase();

  const confirm = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${target.user_id}`, { data: { pin, confirm_email: confirmEmail.trim() } });
      toast.success(`Deleted ${target.name || target.email}. Recoverable for 30 days.`);
      onDeleted(target.user_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-900 border-white/10">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <WarningCircle size={18} className="text-red-400" /> Delete this user?
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            This removes <span className="text-zinc-200">{target?.name || target?.email}</span>, all workspaces they own, and those workspaces' machines, members and invites. Agents on their machines will stop connecting. The account is <span className="text-zinc-200">recoverable for 30 days</span> from Recently Deleted.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Type the account email to confirm</label>
            <Input
              data-testid="delete-confirm-email"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={target?.email}
              className="bg-black border-zinc-800 focus-visible:ring-red-600 font-mono"
            />
          </div>
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Enter your delete PIN</label>
            <Input
              data-testid="delete-pin"
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Delete PIN"
              className="bg-black border-zinc-800 focus-visible:ring-red-600"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-transparent border-white/10 hover:bg-zinc-800" data-testid="cancel-delete-user">Cancel</Button>
          <Button
            onClick={confirm}
            disabled={deleting || !emailMatches || !pin}
            className="bg-red-600 hover:bg-red-500 disabled:opacity-40"
            data-testid="confirm-delete-user"
          >
            {deleting ? "Deleting…" : "Delete user"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
